import React from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LABEL_KEYS: Record<string, string> = {
  home: 'breadcrumb.home',
  profile: 'breadcrumb.profile',
  admin: 'breadcrumb.admin',
  overview: 'breadcrumb.overview',
  users: 'breadcrumb.users',
  classrooms: 'breadcrumb.classrooms',
  'my-classrooms': 'breadcrumb.myClassrooms',
  assignments: 'breadcrumb.assignments',
  students: 'breadcrumb.students',
  names: 'breadcrumb.names',
  create: 'breadcrumb.create',
  settings: 'breadcrumb.settings',
  practice: 'breadcrumb.practice',
  content: 'breadcrumb.content',
  sources: 'breadcrumb.sources',
  activities: 'breadcrumb.activities',
  'ai-settings': 'breadcrumb.aiSettings',
  'ai-usage': 'breadcrumb.aiUsage',
  'system-health': 'breadcrumb.systemHealth',
  tags: 'breadcrumb.tags',
  'content-folders': 'breadcrumb.contentFolders'
};

interface BreadcrumbsBarProps {
  customSegments?: Array<{ label: string; path: string }>;
}

export default function BreadcrumbsBar({ customSegments }: BreadcrumbsBarProps = {}): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const lastAutoScrollKeyRef = React.useRef<string>('');

  const autoScrollKey = React.useMemo(() => {
    // Key represents the actual breadcrumb content (not object identity)
    return `${location.pathname}::${JSON.stringify(customSegments || [])}`;
  }, [customSegments, location.pathname]);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (lastAutoScrollKeyRef.current === autoScrollKey) return;
    lastAutoScrollKeyRef.current = autoScrollKey;

    // Auto-scroll to the right-most only when breadcrumb content changes.
    el.scrollLeft = el.scrollWidth;
  }, [autoScrollKey]);

  if (customSegments && customSegments.length > 0) {
    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          paddingBottom: '4px'
        }}
      >
        <Breadcrumb
          fontSize={{ base: 'xs', md: 'sm' }}
          color="gray.600"
          maxW="100%"
          w="100%"
          display="flex"
          flexWrap="nowrap"
          whiteSpace="nowrap"
        >
          <BreadcrumbItem flexShrink={0}>
            <BreadcrumbLink
              as={Link}
              to="/"
              whiteSpace="nowrap"
            >
              {t('breadcrumb.home')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          {customSegments.map((seg, idx) => {
            const isLast = idx === customSegments.length - 1;
            return (
              <BreadcrumbItem
                key={idx}
                isCurrentPage={isLast}
                flexShrink={0}
                minW="auto"
              >
                <BreadcrumbLink
                  as={Link}
                  to={seg.path}
                  whiteSpace="nowrap"
                  title={seg.label}
                >
                  {seg.label}
                </BreadcrumbLink>
              </BreadcrumbItem>
            );
          })}
        </Breadcrumb>
      </div>
    );
  }

  const segments = location.pathname.split('/').filter(Boolean);
  const paths = segments.map((_, idx) => '/' + segments.slice(0, idx + 1).join('/'));

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        paddingBottom: '4px'
      }}
    >
      <Breadcrumb
        fontSize={{ base: 'xs', md: 'sm' }}
        color="gray.600"
        maxW="100%"
        w="100%"
        display="flex"
        flexWrap="nowrap"
        whiteSpace="nowrap"
      >
        <BreadcrumbItem flexShrink={0}>
          <BreadcrumbLink
            as={Link}
            to="/"
            whiteSpace="nowrap"
          >
            {t('breadcrumb.home')}
          </BreadcrumbLink>
        </BreadcrumbItem>
        {segments.map((seg, idx) => {
          const label = LABEL_KEYS[seg] ? t(LABEL_KEYS[seg]) : decodeURIComponent(seg);
          const isLast = idx === segments.length - 1;
          return (
            <BreadcrumbItem
              key={idx}
              isCurrentPage={isLast}
              flexShrink={0}
              minW="auto"
            >
              <BreadcrumbLink
                as={Link}
                to={paths[idx]}
                whiteSpace="nowrap"
                title={label}
              >
                {label}
              </BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
    </div>
  );
}
